import { AssetData, SavedAssetData } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { PlayPauseIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';
import AlertModal from '../../components/Common/AlertModal';
import Button from '../../components/Common/Button';
import IconButton from '../../components/Common/IconButton';
import Loader from '../../components/Common/Loader';
import MenuButton from '../../components/Common/MenuButton';
import TextField from '../../components/Common/TextField';
import { UserContext } from '../../contexts/UserContext';
import { useDebounce } from '../../hooks/useDebounce';
import { usePrevious } from '../../hooks/usePrevious';
import {
  deleteAssetFromLibrary,
  fetchAssets,
  markUsed,
  updateAsset,
} from '../../services/ImagesService';
import { classNames, distinct } from '../../utils';
import { assetUrlToCDNUrl } from '../../utils/assets';
import { handleUpload } from '../../utils/file-upload';
import AddEmbededVideoAssetModal from './AddEmbededVideoAssetModal';
import ImageLibraryModalWrapper from './ImageLibraryModalWrapper';

interface Props {
  multi?: boolean;
  open: boolean;
  types?: ('image' | 'video')[];
  onClose: () => void;
  onSelected: (images: AssetData[]) => void;
}

const pageSize = 20;

export default function ImageLibraryModal({
  multi,
  open,
  types = ['image'],
  onClose,
  onSelected,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<
    'Library' | 'Unsplash' | 'Pexels' | 'Upload'
  >('Library');
  const prevActiveTab = usePrevious(activeTab);
  const [selectedImages, setSelectedImages] = useState<AssetData[]>([]);

  const [, setLoadingNextPage] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const [images, setImages] = useState<AssetData[] | null>(null);

  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState<{
    selectedImage: AssetData;
  }>();
  const [renameImageModalOpen, setRenameImageModalOpen] = useState<{
    selectedImage: AssetData;
    newName?: string;
  }>();
  const [addEmbededVideoModalOpen, setAddEmbededVideoModalOpen] = useState<{
    open: boolean;
  }>();

  const tabs: {
    name: 'Library' | 'Unsplash' | 'Pexels' | 'Upload';
    href: string;
  }[] = [
    { name: 'Library', href: '#' },
    { name: 'Unsplash', href: '#' },
    { name: 'Pexels', href: '#' },
    { name: 'Upload', href: '#' },
  ];

  useEffect(() => {
    if (!selectedAccount) return;
    if (!open) {
      setActiveTab('Library');
      setSelectedImages([]);
      setSearch('');
      setImages(null);
      return;
    }
    if (activeTab !== prevActiveTab) {
      setImages(null);
      setSelectedImages([]);
    }

    let ok = true;

    setLoadingNextPage(true);

    fetchAssets(
      selectedAccount,
      activeTab,
      debouncedSearch,
      undefined,
      pageSize,
      types,
    )
      .then((data) => {
        if (!ok) return;
        setImages(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        console.error('Error fetching images:', e);
      })
      .finally(() => {
        setLoadingNextPage(false);
      });

    return () => {
      ok = false;
    };
  }, [debouncedSearch, open, activeTab, prevActiveTab, selectedAccount, types]);

  const nextPage = useCallback(() => {
    if (!selectedAccount) return;
    if (!images) return;
    if (images.length % pageSize !== 0) return;

    setLoadingNextPage(true);

    const page = images.length / pageSize + 1;
    fetchAssets(selectedAccount, activeTab, debouncedSearch, page, pageSize)
      .then((data) => {
        setImages((existing) => [...(existing || []), ...data]);
      })
      .catch((error) => {
        console.error('Error fetching images:', error);
        Sentry.captureException(error);
      })
      .finally(() => {
        setLoadingNextPage(false);
      });
  }, [selectedAccount, images, activeTab, debouncedSearch]);

  const versionsListContainerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: Math.ceil((images?.length ?? 0) / 4.0) + 1,
    estimateSize: () => 204,
    getScrollElement: () => versionsListContainerRef.current,
    paddingEnd: 50,
    paddingStart: 4,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <ImageLibraryModalWrapper open={open} onClose={onClose}>
      <AlertModal
        type="warn"
        title={`Are you sure you want to delete ${deleteImageModalOpen?.selectedImage?.alt || 'this image'}?`}
        description="This will not affect pages that are already using this image. This action cannot be undone."
        open={!!deleteImageModalOpen}
        onClose={() => setDeleteImageModalOpen(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setDeleteImageModalOpen(undefined),
          },
          {
            type: 'primary',
            title: 'Delete',
            color: 'negative',
            onClick: () => {
              if (!selectedAccount) return;
              const selectedImage = deleteImageModalOpen?.selectedImage;
              if (!selectedImage) return;

              const oldPosition = images?.findIndex(
                (i) => i.id === selectedImage.id,
              );
              setImages(
                (images) =>
                  images?.filter((i) => i.id !== selectedImage.id) || null,
              );

              deleteAssetFromLibrary(selectedAccount, selectedImage.id).catch(
                (e) => {
                  console.error(e);
                  Sentry.captureException(e);

                  setImages((images) => {
                    if (oldPosition !== undefined && images) {
                      const newImages = [...images];
                      newImages.splice(oldPosition, 0, selectedImage);
                      return newImages;
                    }
                    return images;
                  });
                },
              );

              setDeleteImageModalOpen(undefined);
            },
          },
        ]}
      />

      <AlertModal
        type="info"
        title="Rename image"
        open={!!renameImageModalOpen}
        onClose={() => setRenameImageModalOpen(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setRenameImageModalOpen(undefined),
          },
          {
            type: 'primary',
            title: 'Save',
            color: 'primary',
            onClick: () => {
              if (!selectedAccount) return;
              const selectedImage = renameImageModalOpen?.selectedImage;
              const newName = renameImageModalOpen?.newName;
              if (!selectedImage || !newName || selectedImage.alt === newName) {
                setRenameImageModalOpen(undefined);
                return;
              }

              const oldName = selectedImage.alt;
              setImages(
                (images) =>
                  images?.map((i) =>
                    i.id === selectedImage.id ? { ...i, alt: newName } : i,
                  ) || null,
              );

              updateAsset(selectedAccount, selectedImage.id, {
                alt: newName,
              }).catch((e) => {
                console.error(e);
                Sentry.captureException(e);

                setImages(
                  (images) =>
                    images?.map((i) =>
                      i.id === selectedImage.id
                        ? { ...i, alt: oldName || '' }
                        : i,
                    ) || null,
                );
              });

              setRenameImageModalOpen(undefined);
            },
          },
        ]}
      >
        <TextField
          value={renameImageModalOpen?.newName || ''}
          onChange={(e) =>
            setRenameImageModalOpen((s) =>
              s
                ? {
                    ...s,
                    newName: e.target.value,
                  }
                : undefined,
            )
          }
          fullWidth
        />
      </AlertModal>
      <AddEmbededVideoAssetModal
        open={addEmbededVideoModalOpen?.open || false}
        onClose={() => setAddEmbededVideoModalOpen(undefined)}
        onSave={(image) => {
          setAddEmbededVideoModalOpen(undefined);
          setSelectedImages([image]);
          setSearch(search);
        }}
      />
      <div className="flex h-full flex-col gap-4 overflow-auto">
        <div className="sticky top-0 z-20 flex flex-col bg-white px-4 pb-4 pt-6 shadow-md">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle
              as="h3"
              className="py-0 text-base font-semibold leading-6 text-gray-900"
            >
              Assets Library
            </DialogTitle>

            <MenuButton
              placement="bottom-end"
              items={[
                ...(types.includes('video') && activeTab === 'Library'
                  ? [
                      {
                        title: 'Add Embeded Video',
                        onClick: () => {
                          setAddEmbededVideoModalOpen({ open: true });
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </div>
          <div className="mt-6 sm:hidden">
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              defaultValue={activeTab}
              onChange={(e) => setActiveTab(e.target.value as any)}
            >
              {tabs.map((tab) => (
                <option key={tab.name}>{tab.name}</option>
              ))}
            </select>
          </div>
          <div className="mt-6 hidden sm:block">
            <nav className="flex space-x-4" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveTab(tab.name)}
                  className={classNames(
                    activeTab === tab.name
                      ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-50'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                    'rounded-md px-3 py-2 text-sm font-medium',
                  )}
                  aria-current={activeTab ? 'page' : undefined}
                  onChange={() => setActiveTab(tab.name)}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
          {activeTab !== 'Upload' ? (
            <>
              <div className="relative mt-3">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-300" />
                </div>
              </div>

              <TextField
                value={search}
                onChange={(v) => setSearch(v.target.value)}
                placeholder="Image description..."
                className="mt-4"
              />
            </>
          ) : (
            <></>
          )}
        </div>
        <div className="flex-1 px-3">
          {activeTab !== 'Upload' ? (
            <>
              {images == null ? <LoadingImages /> : <></>}
              {images != null && !images.length ? <>No images found</> : <></>}
              {images != null && !!images.length && (
                <div
                  ref={versionsListContainerRef}
                  className="h-full w-full overflow-y-auto contain-strict"
                >
                  <div
                    style={{
                      height: virtualizer.getTotalSize(),
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                      }}
                    >
                      {virtualItems.map((virtualItem) => {
                        const rowIndex = virtualItem.index * 4;
                        const rowImages = images?.slice(rowIndex, rowIndex + 4);
                        if (
                          !rowImages.length &&
                          images.length &&
                          images.length % pageSize === 0
                        ) {
                          return (
                            <InView
                              key={virtualItem.key}
                              as="div"
                              className="col-span-1 flex h-48 items-center justify-center p-9 sm:col-span-2 md:col-span-4 lg:col-span-4"
                              onChange={(inView) => {
                                if (!inView) return;
                                nextPage();
                              }}
                            >
                              <Loader />
                            </InView>
                          );
                        }
                        return (
                          <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            className="grid grid-cols-4 gap-x-3 px-1 pb-3"
                          >
                            {rowImages?.map((image) => {
                              const selectedIndex = selectedImages.findIndex(
                                (s) => s.id === image.id,
                              );
                              const isSelected = selectedIndex !== -1;
                              return (
                                <div className="relative" key={image.id}>
                                  <AssetItem
                                    isSelected={isSelected}
                                    activeTab={activeTab}
                                    image={image}
                                    selectedIndex={selectedIndex}
                                    selectedImages={selectedImages}
                                    onClick={() => {
                                      if (multi) {
                                        setSelectedImages((images) =>
                                          isSelected
                                            ? images.filter(
                                                (s) => s.id !== image.id,
                                              )
                                            : distinct(
                                                [...images, image],
                                                (a) => a.id,
                                              ),
                                        );
                                      } else {
                                        setSelectedImages([image]);
                                      }
                                    }}
                                    onRename={() =>
                                      setRenameImageModalOpen({
                                        selectedImage: image,
                                        newName: image.alt,
                                      })
                                    }
                                    onDelete={() => {
                                      setDeleteImageModalOpen({
                                        selectedImage: image,
                                      });
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // <ul
            //   ref={versionsListContainerRef}
            //   role="list"
            //   className="grid grid-cols-1 gap-x-3 gap-y-3 pb-20 sm:grid-cols-2 sm:gap-x-3 md:grid-cols-4 lg:grid-cols-4 xl:gap-x-3"
            // >
            //   {virtualizer.getVirtualItems()?.map((virtualItem) => {
            //     const image = images?.[virtualItem.index];
            //     if (!image) return null;
            //     const selectedIndex = selectedImages.findIndex(
            //       (s) => s.id === image.id,
            //     );
            //     const isSelected = selectedIndex !== -1;
            //     return (
            //       <li
            //         key={image.id}
            //         className="relative"
            //         ref={virtualizer.measureElement}
            //       >
            //         <AssetItem
            //           isSelected={isSelected}
            //           activeTab={activeTab}
            //           image={image}
            //           selectedIndex={selectedIndex}
            //           selectedImages={selectedImages}
            //           onClick={() => {
            //             if (multi) {
            //               setSelectedImages((images) =>
            //                 isSelected
            //                   ? images.filter((s) => s.id !== image.id)
            //                   : distinct([...images, image], (a) => a.id),
            //               );
            //             } else {
            //               setSelectedImages([image]);
            //             }
            //           }}
            //           onRename={() =>
            //             setRenameImageModalOpen({
            //               selectedImage: image,
            //               newName: image.alt,
            //             })
            //           }
            //           onDelete={() => {
            //             setDeleteImageModalOpen({
            //               selectedImage: image,
            //             });
            //           }}
            //         />
            //       </li>
            //     );
            //   })}
            //   {images && images.length && images.length % pageSize === 0 ? (
            //     <InView
            //       as="div"
            //       className="col-span-1 flex h-48 items-center justify-center p-9 sm:col-span-2 md:col-span-4 lg:col-span-4"
            //       onChange={(inView) => {
            //         if (!inView) return;
            //         nextPage();
            //       }}
            //     >
            //       <Loader />
            //     </InView>
            //   ) : (
            //     <></>
            //   )}
            //   {images == null ? <LoadingImages /> : <></>}
            //   {images != null && !images.length ? <>No images found</> : <></>}
            // </ul>

            <Upload
              onUploaded={(selected) => {
                if (multi || selected.length === 1) {
                  onSelected(selected);
                } else {
                  setActiveTab('Library');
                }
              }}
              types={types}
            />
          )}
        </div>
        <div className="fixed bottom-0 left-0 flex w-full flex-row justify-end gap-4 bg-white px-4 py-3">
          <Button variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            disabled={!selectedImages.length}
            onClick={() => {
              if (!selectedImages.length) return;

              for (const selectedImage of selectedImages) {
                if (
                  'downloadLocation' in selectedImage &&
                  selectedImage.downloadLocation &&
                  typeof selectedImage.downloadLocation === 'string'
                ) {
                  markUsed('unsplash', selectedImage.downloadLocation);
                  delete selectedImage.downloadLocation;
                }
              }

              onSelected(selectedImages);
            }}
          >
            Insert
            {multi && selectedImages.length > 1
              ? ` ${selectedImages.length}`
              : ''}
          </Button>
        </div>
      </div>
    </ImageLibraryModalWrapper>
  );
}

const AssetItem = ({
  isSelected,
  activeTab,
  image,
  selectedIndex,
  selectedImages,
  onClick,
  onRename,
  onDelete,
}: {
  isSelected: boolean;
  activeTab: 'Library' | 'Unsplash' | 'Pexels' | 'Upload';
  image: AssetData;
  selectedIndex: number;
  selectedImages: AssetData[];
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
}) => {
  return (
    <>
      <div
        onClick={onClick}
        className={classNames(
          'aspect-h-7 aspect-w-10 group block h-48 w-full cursor-pointer overflow-hidden rounded-lg bg-gray-100',
          isSelected
            ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-gray-100'
            : '',
        )}
      >
        {image.type !== 'video' && (
          <img
            src={assetUrlToCDNUrl(image.thumbnail || image.url)}
            alt={image.alt}
            className="disable-user-select pointer-events-none h-full w-full object-contain group-hover:opacity-75"
          />
        )}
        {image.type === 'video' && image.source !== 'vimeo' && (
          <div className="relative flex h-full items-center">
            <video
              id={`video-${image.id}`}
              src={assetUrlToCDNUrl(image?.url)}
              controls={false}
              className="disable-user-select pointer-events-none h-full w-full object-contain group-hover:opacity-75"
            ></video>
            <IconButton
              color="positive"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                const video = document.querySelector(
                  `#video-${image.id}`,
                ) as HTMLVideoElement;
                if (video) {
                  video.paused ? video.play() : video.pause();
                }
              }}
            >
              <PlayPauseIcon className="h-4 w-4" />
            </IconButton>
          </div>
        )}
        {image.type === 'video' && image.source === 'vimeo' && (
          <div className="relative flex h-full items-center">
            <iframe src={image?.url} className="h-full w-full" />
          </div>
        )}
        {activeTab === 'Library' && (
          <div className="absolute inset-x-0 bottom-0 flex w-full flex-row rounded-b-lg bg-gray-300/50 px-2 py-1 text-gray-900 lg:opacity-0 lg:group-hover:opacity-100">
            <span className="flex-1 truncate text-sm">{image.alt || ''}</span>
            <MenuButton
              buttonClassName="h-6 w-6"
              items={[
                {
                  id: 'rename',
                  title: 'Rename',
                  onClick: onRename,
                },
                {
                  id: 'delete',
                  title: 'Delete',
                  destructive: true,
                  onClick: onDelete,
                },
              ]}
            />
          </div>
        )}
        {selectedIndex !== -1 && selectedImages.length > 1 ? (
          <span className="disable-user-select absolute right-1 top-1 z-10 h-5 min-w-[1.25rem] rounded-full bg-indigo-600 text-center align-middle text-sm text-white shadow">
            {selectedIndex + 1}
          </span>
        ) : (
          <></>
        )}
        <button type="button" className="inset-0 focus:outline-none">
          <span className="sr-only">View details for {image.id}</span>
        </button>
      </div>
      {activeTab !== 'Library' ? (
        <p className="mt-2 block text-xs font-medium text-gray-500">
          Photo by{' '}
          <a
            href={image.photographerUrl}
            target="_blank"
            className="z-20 text-blue-500 underline"
            rel="noreferrer"
          >
            {image.photographer || activeTab}
          </a>{' '}
          on{' '}
          {activeTab === 'Pexels' ? (
            <a
              href={'https://pexels.com'}
              target="_blank"
              className="z-20 text-blue-500 underline"
              rel="noreferrer"
            >
              Pexels
            </a>
          ) : (
            <></>
          )}
          {activeTab === 'Unsplash' ? (
            <a
              href={'https://unsplash.com'}
              target="_blank"
              className="z-20 text-blue-500 underline"
              rel="noreferrer"
            >
              Unsplash
            </a>
          ) : (
            <></>
          )}
        </p>
      ) : (
        <></>
      )}
    </>
  );
};

const LoadingImages = () => {
  return (
    <div className="grid grid-cols-1 gap-x-3 gap-y-3 px-1 pb-20 pt-1 sm:grid-cols-2 sm:gap-x-3 md:grid-cols-4 lg:grid-cols-4 xl:gap-x-3">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((v) => (
        <div
          key={'key-' + v}
          role="status"
          className='md:items-center"> animate-pulse space-y-8 md:flex md:space-x-8 md:space-y-0'
        >
          <div className="flex h-48 w-full items-center justify-center rounded bg-gray-300">
            <svg
              className="h-10 w-10 text-gray-200"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 18"
            >
              <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
};

const UploadProgressBar = ({ progress }: { progress: number }) => {
  return (
    <div className="w-full rounded-full bg-gray-200">
      <div
        className="min-w-[20px] rounded-full bg-blue-600 p-0.5 text-center text-xs font-medium leading-none text-blue-100 transition-all"
        style={{ width: progress + '%' }}
      >
        {progress}%
      </div>
    </div>
  );
};

const Upload = ({
  onUploaded,
  types,
}: {
  onUploaded: (images: SavedAssetData[]) => void;
  types: ('image' | 'video')[];
}) => {
  const { selectedAccount } = useContext(UserContext);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileSelection = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!selectedAccount) return;

      if (e.target.files) {
        setUploadProgress(0);
        handleUpload(
          selectedAccount.id,
          Array.from(e.target.files),
          (progress) => setUploadProgress(Math.ceil(progress)),
        )
          .then((res) => {
            setUploadProgress(null);
            onUploaded(res);
          })
          .catch((e) => {
            Sentry.captureException(e);
            setUploadProgress(null);
            console.error(e);
          });
      }
    },
    [onUploaded, selectedAccount],
  );

  const handleFileDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!selectedAccount) return;

      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        setUploadProgress(0);
        handleUpload(
          selectedAccount.id,
          Array.from(e.dataTransfer.files),
          (progress) => setUploadProgress(Math.ceil(progress)),
        )
          .then((res) => {
            setUploadProgress(null);
            onUploaded(res);
          })
          .catch((e) => {
            Sentry.captureException(e);
            setUploadProgress(null);
            console.error(e);
          });
      }
    },
    [onUploaded, selectedAccount],
  );

  return (
    <>
      {uploadProgress == null ? (
        <div className="col-span-full flex h-full items-center justify-center">
          <div
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleFileDrop}
            className={classNames(
              'mt-2 flex min-h-[50%] w-full max-w-[90%] justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10',
              dragActive ? 'bg-blue-100' : '',
            )}
          >
            <div className="flex flex-col justify-center text-center">
              <PhotoIcon
                className="mx-auto h-12 w-12 text-gray-300"
                aria-hidden="true"
              />
              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer rounded-md font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                >
                  <span>Upload a file</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    multiple
                    accept={types
                      .map((t) => (t === 'image' ? 'image/*' : 'video/*'))
                      .join(',')}
                    className="sr-only"
                    onChange={handleFileSelection}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs leading-5 text-gray-600">
                {types
                  .map((t) => (t === 'image' ? 'PNG, JPG, GIF' : 'MP4'))
                  .join(', ')}{' '}
                up to 10MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <UploadProgressBar progress={uploadProgress} />
        </>
      )}
    </>
  );
};
